import React, { FC, useContext, useMemo } from 'react';
import { InvestmentsContext } from '../../../../context/investmentContext';
import { DataGrid, Paginate } from '@devd-client/devd/components';
import styled from 'styled-components';
import { createColumnHelper } from '@tanstack/react-table';
import { Box } from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { TicketDataType } from '../../../../helpers/Investments.types';

interface DataTableProps {
  handlePageClick: (event: any) => void;
  isFetching: boolean;
}

const AnchorLink = styled.a`
  text-decoration: underline;
  color: ${({ theme }) => theme.colors.primary};
`;

const DataTable: FC<DataTableProps> = ({ handlePageClick, isFetching }) => {
  const {
    state: { ticketData },
  } = useContext(InvestmentsContext);

  const rows: TicketDataType[] = useMemo(
    () => ticketData?.data || [],
    [ticketData?.data]
  );

  const columnHelper = createColumnHelper<TicketDataType>();

  const columns = useMemo(
    () => [
      columnHelper.accessor('key', {
        cell: (info) => (
          <AnchorLink
            href={info.row.original.ticketUrl}
            target="_blank"
            rel="noreferrer"
          >
            {info.getValue()}
          </AnchorLink>
        ),
        header: 'Key',
      }),
      columnHelper.accessor('summary', {
        cell: (info) => info.getValue(),
        header: 'Summary',
        size: 360,
      }),
      columnHelper.accessor('updatedDate', {
        cell: (info) => info.getValue(),
        header: 'Date',
      }),
      columnHelper.accessor('assignee', {
        cell: (info) => {
          const value = info.getValue();
          if (!value) return <Box textAlign="center">-</Box>;
          return (
            <Link
              to={`/contributors/${value}`}
              style={{ textDecoration: 'underline' }}
            >
              {value}
            </Link>
          );
        },
        header: 'Assignee',
      }),

      columnHelper.accessor('ticketType', {
        cell: (info) => info.getValue(),
        header: 'Type',
      }),
    ],
    [ticketData?.data]
  );

  return (
    <Box>
      <DataGrid
        showLoader={isFetching}
        maxH="20rem"
        columns={columns}
        data={rows}
        sticky="firstCol"
      />

      <Paginate
        currentPage={ticketData?.currentPage}
        totalPageCount={ticketData?.totalPages}
        pageSize={20}
        onPageChange={handlePageClick}
      />
    </Box>
  );
};

export default React.memo(DataTable);
